<template>
  <iDropdown
    :show-dropdown="isOpen"
    :z-index="2200"
    :overlay-position="isDock ? 'top-left' : 'bottom-left'"
    :surface="isDock ? 'bare' : 'card'"
    :use-trigger-width="!compact"
    width="fill"
    @close="isOpen = false"
  >
    <template #trigger>
      <iRow
        gap="small"
        vertical-align="middle"
        wrap="nowrap"
        width="fill"
        :clickable="!disabled"
        :data-test="testId"
        :data-tone="tone"
        :style-overrides="triggerStyles"
        @click="toggle"
      >
        <iIcon
          :name="icon"
          size="small"
          :variant="isDock ? 'light' : 'subtle'"
          :tooltip="compact ? `${label}: ${selectedName}` : ''"
          disable-hover
        />
        <iColumn
          v-if="!compact"
          width="fill"
          gap="none"
          :min-width="0"
          overflow="hidden"
        >
          <iText v-if="tone === 'card'" font-size="verySmall" variant="subtle">
            {{ label }}
          </iText>
          <iText
            font-size="small"
            width="fill"
            ellipsis
            :wrap="false"
            :variant="textVariant"
            :aria-label="tone === 'card' ? undefined : `${label}: ${selectedName}`"
            :tooltip="selectedName"
            data-test="device-menu-label"
          >
            {{ selectedName }}
          </iText>
        </iColumn>
        <slot name="trailing" />
        <iIcon
          v-if="!disabled"
          :name="isDock ? 'ic:baseline-arrow-drop-down' : 'ic:baseline-expand-more'"
          size="small"
          :variant="isDock ? 'light' : 'subtle'"
          disable-hover
        />
      </iRow>
    </template>
    <template #overlay>
      <iControlMenu v-if="isDock">
        <iControlMenuItem
          v-for="item in items"
          :key="item.id"
          :selected="item.id === selectedId"
          :data-test="`${testId}-option`"
          @click="select(item)"
        >
          <iText
            font-size="small"
            ellipsis
            width="fill"
            variant="light"
          >
            {{ item.name }}
          </iText>
          <iIcon
            v-if="item.id === selectedId"
            name="ic:baseline-check"
            size="small"
            variant="inherit"
            disable-hover
          />
        </iControlMenuItem>
      </iControlMenu>
      <iColumn
        v-else
        gap="none"
        width="fill"
        wrap="nowrap"
        :min-width="compact ? 260 : 0"
        :style-overrides="overlayStyles"
      >
        <iRow
          v-for="item in items"
          :key="item.id"
          gap="small"
          vertical-align="middle"
          wrap="nowrap"
          width="fill"
          clickable
          :data-test="`${testId}-option`"
          :style-overrides="{
            backgroundColor: item.id === selectedId ? 'subtle' : 'none',
            paddingTop: 'small',
            paddingBottom: 'small',
            paddingLeft: 'standard',
            paddingRight: 'standard',
          }"
          @click="select(item)"
        >
          <iText
            font-size="small"
            ellipsis
            width="fill"
            variant="standard"
          >
            {{ item.name }}
          </iText>
          <iIcon
            v-if="item.id === selectedId"
            name="ic:baseline-check"
            size="small"
            variant="branded"
            disable-hover
          />
        </iRow>
      </iColumn>
    </template>
  </iDropdown>
</template>

<script>
const TONES = ["card", "dock", "pill"];

export default {
  name: "DeviceMenu",
  props: {
    icon: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    selectedId: {
      type: String,
      default: null,
    },
    emptyState: {
      type: String,
      default: "",
    },
    testId: {
      type: String,
      default: "",
    },
    tone: {
      type: String,
      default: "card",
      validator: tone => TONES.includes(tone),
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["select"],
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    isDock() {
      return this.tone === "dock";
    },
    disabled() {
      return this.items.length === 0;
    },
    selectedName() {
      const selected = this.items.find(item => item.id === this.selectedId);
      if (selected) {
        return selected.name;
      }

      return this.items.length ? this.items[0].name : this.emptyState;
    },
    textVariant() {
      if (this.isDock) {
        return "light";
      }

      return this.items.length ? "standard" : "subtle";
    },
    triggerStyles() {
      if (this.isDock) {
        return {
          backgroundColor: "none",
          paddingTop: "small",
          paddingBottom: "small",
          paddingLeft: "small",
          paddingRight: "extraSmall",
        };
      }

      if (this.tone === "pill") {
        return {
          backgroundColor: "subtle",
          borderRadius: "large",
          paddingTop: "small",
          paddingBottom: "small",
          paddingLeft: "standard",
          paddingRight: "small",
        };
      }

      return {
        backgroundColor: "paper",
        borderWidth: "thin",
        borderColor: "light",
        borderRadius: "large",
        paddingTop: "small",
        paddingBottom: "small",
        paddingLeft: "standard",
        paddingRight: "standard",
      };
    },
    overlayStyles() {
      return {
        backgroundColor: "paper",
        borderRadius: "standard",
        borderWidth: "thin",
        borderColor: "light",
        paddingTop: "extraSmall",
        paddingBottom: "extraSmall",
      };
    },
  },
  methods: {
    toggle() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen;
      }
    },
    close() {
      this.isOpen = false;
    },
    select(item) {
      this.isOpen = false;
      this.$emit("select", item.id);
    },
  },
};
</script>
